import type { DownloadedFile } from '@/lib/api-client';
import { saveBlob } from '@/lib/save-blob';
import {
  downloadInstrumentRunSamplesCsv,
  downloadInstrumentRunsCsv,
  type InstrumentRunsDownloadOptions,
} from '@/services/instrumentRuns';
import { downloadStudySamplesCsv, type StudySamplesDownloadOptions } from '@/services/samples';
import { useMutation } from '@tanstack/react-query';

// A mutation, not a query: downloads are user-triggered and must never be cached or refetched.
function useFileDownload<TVars>(fetcher: (vars: TVars) => Promise<DownloadedFile>, fallbackFilename: string) {
  return useMutation<string, Error, TVars>({
    mutationFn: async vars => {
      const { blob, filename } = await fetcher(vars);
      const resolved = filename ?? fallbackFilename;
      saveBlob(blob, resolved);
      return resolved;
    },
  });
}

/** CSV export of a study's samples, honouring the current sort and filters. */
export function useStudySamplesCsvDownload(orgId: string, studyId: string) {
  return useFileDownload<StudySamplesDownloadOptions | undefined>(
    options => downloadStudySamplesCsv(orgId, studyId, options),
    `study_samples_${studyId}.csv`,
  );
}

/** CSV export of an instrument run's samples. */
export function useInstrumentRunSamplesCsvDownload(orgId: string, runId: string) {
  return useFileDownload<void>(
    () => downloadInstrumentRunSamplesCsv(orgId, runId),
    `instrument_run_samples_${runId}.csv`,
  );
}

/** CSV export of an org's instrument runs, honouring the current sort and filters. */
export function useInstrumentRunsCsvDownload(orgId: string) {
  return useFileDownload<InstrumentRunsDownloadOptions | undefined>(
    options => downloadInstrumentRunsCsv(orgId, options),
    `instrument_runs_${orgId}.csv`,
  );
}
